import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedInventory from '../../../../components/RelatedInventory';

const PorscheService = () => {
  const porscheFeatures = [
    'Porsche expert technicians',
    'PIWIS Diagnostic System',
    'Track Performance Specialists',
    'Genuine Porsche Parts',
    'All Porsche Models',
    'Performance Tuning Experts'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const commonIssues = [
    { 
      model: '911 (996/997/991)', 
      years: '1999-Present',
      issues: ['IMS bearing (early models)', 'RMS leaks', 'Bore scoring', 'Coolant pipes'],
      solution: 'Preventive IMS upgrade and comprehensive inspection'
    },
    {
      model: 'Boxster/Cayman (986/987/981)',
      years: '197-Present',
      issues: ['IMS bearing', 'AOS failure', 'Water pump', 'Clutch wear'],
      solution: 'Regular maintenance and early intervention'
    },
    {
      model: 'Cayenne/Macan',
      years: '2003-Present',
      issues: ['Coolant pipes', 'Transfer case', 'Air suspension', 'Driveshaft support'],
      solution: 'Specialized SUV service and preventive maintenance'
    },
    {
      model: 'Panamera/Taycan',
      years: '2010-Present',
      issues: ['PDK transmission', 'Air suspension', 'Hybrid systems', 'Electronic modules'],
      solution: 'Advanced diagnostic and software updates'
    }
  ];

  const services = [
    {
      title: 'Maintenance Services',
      items: [
        'Minor & Major Services',
        'Oil Changes (Dry Sump)',
        'Brake Service',
        'PDK Transmission Service',
        'Spark Plug Replacement'
      ]
    },
    {
      title: 'Performance Services',
      items: [
        'Track Preparation',
        'Suspension Tuning',
        'Brake Upgrades',
        'Exhaust Systems',
        'ECU Tuning'
      ]
    },
    {
      title: 'Specialized Repair',
      items: [
        'IMS Bearing Upgrade',
        'RMS Seal Replacement',
        'Engine Rebuilds',
        'PDK Clutch Service',
        'PASM Suspension'
      ]
    },
    {
      title: 'Diagnostic Services',
      items: [
        'PIWIS Diagnostics',
        'Pre-Purchase Inspection',
        'Performance Analysis',
        'Module Coding',
        'Software Updates'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Porsche Repair Dallas & Fort Worth | Expert Porsche Service DFW | Paradise-Performance</title>
        <meta name="description" content="Porsche repair Dallas & Fort Worth specialists. Expert 911, Cayenne, Boxster, Macan service. PIWIS diagnostics, IMS bearing experts. 30-40% less than dealership. Serving Dallas, Fort Worth, Plano. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/porsche" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/porsche" />
        <meta property="og:title" content="Porsche Repair Dallas & Fort Worth | Expert 911, Cayenne, Boxster Service" />
        <meta property="og:description" content="Porsche repair Dallas & Fort Worth specialists. IMS bearing experts, PIWIS diagnostics, all models. 30-40% less than dealership. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/porsche1.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://paradise-performance.com/services/european-car-repair/porsche" />
        <meta name="twitter:title" content="Porsche Repair Dallas & Fort Worth | Expert 911, Cayenne Service" />
        <meta name="twitter:description" content="Porsche repair Dallas & Fort Worth. IMS bearing experts, PIWIS diagnostics, all models. 30-40% less than dealership." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/porsche1.png" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Porsche Repair Dallas Fort Worth - Paradise-Performance",
            "description": "Expert Porsche repair Dallas and Porsche repair Fort Worth. Specializing in 911, Boxster, Cayenne, Macan, Panamera service. PIWIS diagnostics, IMS bearing repair. Serving Dallas, Fort Worth, Arlington, Plano, Richardson.",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Arlington", "Plano", "Richardson", "Frisco", "McKinney", "Southlake", "Keller", "Grapevine", "Colleyville", "Highland Park", "University Park"],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "89"
            },
            "brand": "Porsche",
            "openingHours": "Mo-Fr 09:00-17:00,Sa 10:00-14:00"
          })}
        </script>
        
        {/* FAQ Schema for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Where can I find Porsche repair in Dallas?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Paradise-Performance offers expert Porsche repair Dallas services just 30 minutes from downtown Dallas. We have PIWIS factory diagnostics, IMS bearing expertise, and service all Porsche models including 911, Cayenne, Boxster, Macan, and Panamera. Save 30-40% vs Dallas Porsche dealerships."
                }
              },
              {
                "@type": "Question",
                "name": "Where can I find Porsche repair in Fort Worth?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Paradise-Performance provides Porsche repair Fort Worth services, located just 25 minutes from downtown Fort Worth. Our expert technicians specialize in all Porsche models with factory-level PIWIS diagnostics. We offer 30-40% savings compared to Park Place Porsche and other dealerships."
                }
              },
              {
                "@type": "Question",
                "name": "How much does Porsche repair cost in Dallas?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Porsche repair in Dallas typically costs 30-40% less at Paradise-Performance compared to dealership prices. For example, an IMS bearing replacement that costs $4,000-5,000 at the dealer is typically $2,800-3,500 at our shop. Oil changes, brake service, and routine maintenance are also significantly less expensive."
                }
              },
              {
                "@type": "Question",
                "name": "Do you have PIWIS diagnostic equipment for Porsche?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we use genuine Porsche PIWIS diagnostic equipment - the same system used by Porsche dealerships. This allows us to perform complete diagnostics, coding, and programming on all Porsche models from 996/986 through current 992 and Taycan."
                }
              },
              {
                "@type": "Question",
                "name": "What Porsche models do you service in Dallas and Fort Worth?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We service all Porsche models at our Dallas/Fort Worth area shop including: 911 (996, 997, 991, 992), Boxster/Cayman (986, 987, 981, 718), Cayenne (955, 957, 958, E3), Macan, Panamera, and Taycan. We specialize in IMS bearing repairs, PDK transmission service, and performance upgrades."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Porsche"
        brandColor="#D5001C"
        tagline="Porsche Repair Dallas & Fort Worth Experts"
        description="Premier Porsche repair Dallas and Porsche repair Fort Worth specialists. Expert service for 911, Cayenne, Boxster, Macan in Dallas, Fort Worth, and all of DFW. PIWIS diagnostics, IMS bearing specialists, complete engine and transmission service."
        features={porscheFeatures}
        heroImage="/assets/assets/images/porsche1.png"
        heroImageAlt="Porsche 911 Performance Vehicle"
        heroVariant="default"
      />

      {/* Emergency Promo Box */}
      <div className="bg-yellow-50 border-2 border-yellow-600 rounded-lg p-6 max-w-4xl mx-auto my-8">
        <h3 className="text-2xl font-bold text-yellow-700 mb-2 text-center">🚨 Limited Same-Day Appointments Available</h3>
        <p className="text-center text-gray-700 mb-4">
          Call <strong className="text-[#D5001C]">(817) 382-2316</strong> now for priority Porsche diagnostics
        </p>
        <div className="text-center">
          <Link to="/contact" className="inline-block bg-yellow-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-yellow-700 transition">
            Book Emergency Repair →
          </Link>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="bg-red-50 border-l-4 border-[#D5001C] p-6 max-w-4xl mx-auto mb-12">
        <h2 className="text-2xl font-bold mb-4">Quick Navigation</h2>
        <div className="grid md:grid-cols-2 gap-2">
          <ul className="space-y-2">
            <li><a href="#porsche-expertise" className="text-[#D5001C] hover:underline">→ Porsche-Specific Expertise</a></li>
            <li><a href="#ims-bearing" className="text-[#D5001C] hover:underline">→ IMS Bearing Specialists</a></li>
            <li><a href="#common-issues" className="text-[#D5001C] hover:underline">→ Common Porsche Issues</a></li>
            <li><a href="#complete-services" className="text-[#D5001C] hover:underline">→ Complete Repair Services</a></li>
            <li><a href="#models-serviced" className="text-[#D5001C] hover:underline">→ Porsche Models We Service</a></li>
          </ul>
          <ul className="space-y-2">
            <li><a href="#vs-dealership" className="text-[#D5001C] hover:underline">→ Paradise vs Dealership</a></li>
            <li><a href="#track-preparation" className="text-[#D5001C] hover:underline">→ Track Day Preparation</a></li>
            <li><a href="#maintenance" className="text-[#D5001C] hover:underline">→ Maintenance Recommendations</a></li>
            <li><a href="#technology" className="text-[#D5001C] hover:underline">→ Understanding Porsche Technology</a></li>
            <li><a href="#faqs" className="text-[#D5001C] hover:underline">→ Frequently Asked Questions</a></li>
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Dallas SEO Hero Section */}
          <motion.div {...fadeInUp} className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Porsche Repair Dallas - Expert Porsche Service Dallas TX</h1>
            <div className="bg-gradient-to-r from-red-50 to-gray-50 border-l-4 border-[#D5001C] rounded-r-lg p-6 mb-8">
              <p className="text-lg text-gray-800 mb-4">
                <strong>Premier Porsche Repair Dallas Location</strong> - Paradise-Performance is the trusted choice for Porsche service in Dallas and surrounding areas. 
                Located just 30 minutes north of Dallas via US-380, we provide dealership-quality Porsche repair at significantly lower prices than Dallas Porsche dealerships.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Why Dallas Porsche Owners Choose Us:</h3>
                  <ul className="space-y-1 text-gray-700">
                    <li>• 30-40% less than Dallas Porsche dealership prices</li>
                    <li>• Expert Porsche technicians</li>
                    <li>• PIWIS diagnostic equipment</li>
                    <li>• Same-day service available</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Serving Dallas Areas:</h3>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Highland Park Porsche owners</li>
                    <li>• North Dallas Porsche service</li>
                    <li>• Plano & Richardson residents</li>
                    <li>• Park Cities Porsche repair</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Intro Section */}
          <motion.div {...fadeInUp} className="mb-12">
            <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
              <div>
                <p className="text-lg text-gray-700 mb-4">
                  Dallas Porsche enthusiasts understand that their prized vehicles represent the pinnacle of German precision engineering, 
                  performance, and design. These high-performance sports cars demand specialized care that standard repair shops simply cannot provide. 
                  At our European auto repair center near Dallas, we offer Porsche owners specialized expertise, professional diagnostics, 
                  and genuine parts that ensure your vehicle maintains its legendary performance, handling, and reliability.
                </p>
              </div>
              <div>
                <img 
                  src="/assets/assets/images/new/porsche-cayennes/porshe-cayennes1.webp" 
                  alt="Porsche 718 Spyder interior" 
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
            </div>

            <h2 id="porsche-expertise" className="text-3xl font-bold text-gray-900 mb-4">Porsche-Specific Expertise You Won't Find at General Repair Shops</h2>
            <p className="text-lg text-gray-700 mb-6">
              Porsche vehicles feature unique engineering approaches that require specialized knowledge and equipment. 
              Our Dallas European auto specialists serving from our Paradise, TX location possess extensive knowledge and experience 
              working specifically with Porsche models. This specialized focus offers critical advantages over general repair facilities:
            </p>

            <div className="mb-8">
              <img 
                src="/assets/assets/images/new/porsche-cayennes/porshe-cayennes2.webp" 
                alt="Proprietary Porsche diagnostic equipment" 
                className="w-full rounded-lg shadow-lg mb-6"
              />
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Porsche-specific PIWIS diagnostic computers with full programming capabilities</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Expert Porsche technicians with model-specific knowledge</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Complete technical database access for all Porsche repair procedures</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Specialized tools designed exclusively for Porsche service operations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Comprehensive knowledge of Porsche's complex electronic systems</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Services Grid */}
          <motion.div {...fadeInUp} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-semibold mb-4 text-[#D5001C]">{service.title}</h3>
                <ul className="space-y-2">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>

          {/* IMS Bearing Special Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-900 to-black rounded-xl p-8 text-white mb-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 id="ims-bearing" className="text-3xl font-bold mb-4">IMS Bearing Specialists</h3>
                <p className="mb-4">
                  The Intermediate Shaft (IMS) bearing is a critical component in many Porsche engines. 
                  Our preventive IMS bearing upgrade service can save you from catastrophic engine failure.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-[#D5001C] mr-2" />
                    <span>Comprehensive inspection included</span>
                  </li>
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-[#D5001C] mr-2" />
                    <span>OEM and upgraded bearing options</span>
                  </li>
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-[#D5001C] mr-2" />
                    <span>Clutch and RMS service available</span>
                  </li>
                </ul>
                <Link
                  to="/contact"
                  className="inline-block bg-[#D5001C] text-white px-6 py-3 rounded font-semibold hover:bg-red-700 transition"
                >
                  Schedule IMS Service
                </Link>
              </div>
              <div className="bg-gray-800 rounded-lg p-6">
                <h4 className="text-xl font-semibold mb-4 text-[#D5001C]">Affected Models</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• 1997-2008 Boxster (986/987)</li>
                  <li>• 1999-2008 911 Carrera (996/997.1)</li>
                  <li>• 2006-2008 Cayman (987)</li>
                  <li>• Excluding Turbo, GT2, GT3</li>
                </ul>
                <p className="mt-4 text-sm text-gray-400">
                  Early intervention is key to preventing engine damage
                </p>
              </div>
            </div>
          </motion.div>

          {/* Common Issues Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h3 id="common-issues" className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Porsche Issues We Address</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {commonIssues.map((issue, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="text-xl font-semibold mb-2">{issue.model}</h4>
                  <p className="text-gray-600 mb-3">Years: {issue.years}</p>
                  <div className="mb-4">
                    <p className="font-semibold text-gray-800 mb-2">Common Issues:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      {issue.issues.map((item, idx) => (
                        <li key={idx} className="text-gray-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-sm text-green-600 font-medium">{issue.solution}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Testimonial 1 */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 mb-16">
            <blockquote className="text-xl italic text-gray-700 mb-4 text-center">
              "I've driven my Porsche 911 to several European repair shops in Dallas, but none could properly diagnose the intermittent hesitation issues I was experiencing. The team at Paradise identified a failing MAF sensor and coil packs that others missed. The drive from Dallas was absolutely worth it for their Porsche-specific knowledge."
            </blockquote>
            <p className="text-center font-bold text-gray-900">— Alex R., Porsche 911 Owner in Dallas</p>
          </motion.div>

          {/* Complete Services Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="complete-services" className="text-3xl font-bold text-gray-900 mb-6">Complete Porsche Repair Services for Dallas-Area Drivers</h2>
            <p className="text-lg text-gray-700 mb-6">
              Our comprehensive <Link to="/services/european-car-repair" className="text-[#D5001C] hover:underline">European auto repair Dallas</Link> services for Porsche owners cover everything from routine maintenance to complex diagnostic and repair procedures. 
              Whether you need a simple <Link to="/services/oil-change" className="text-[#D5001C] hover:underline">Porsche oil change</Link>, expert <Link to="/services/transmission-repair" className="text-[#D5001C] hover:underline">PDK transmission service</Link>, or precision <Link to="/services/suspension-repair" className="text-[#D5001C] hover:underline">PASM suspension repair</Link>, we've got you covered:
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Porsche-specific maintenance schedules and service</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Engine diagnostics and performance optimization</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />PDK and manual transmission service and repair</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Cooling system maintenance and upgrades</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Suspension and alignment services using Porsche specifications</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Brake system service with genuine Porsche or premium aftermarket parts</li>
              </ul>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />IMS bearing replacement and monitoring</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />PCM system diagnostics and updates</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Electrical system troubleshooting and repair</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Air conditioning and climate control system service</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Pre-purchase inspections for used Porsche vehicles</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Performance upgrades (tuning, exhaust, suspension)</li>
              </ul>
            </div>
          </motion.div>

          {/* Models We Service */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="models-serviced" className="text-3xl font-bold text-gray-900 mb-6">Porsche Models We Service for Dallas-Area Owners</h2>
            <p className="text-lg text-gray-700 mb-8">
              Our European auto repair facility serving Dallas from our Paradise, TX location has extensive experience with all Porsche models, from classic air-cooled to the latest water-cooled vehicles:
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-xl font-bold text-[#D5001C] mb-4">Porsche 911 Series</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Air-cooled (1964-1998)</li>
                  <li>• 996 (1999-2004)</li>
                  <li>• 997 (2005-2012)</li>
                  <li>• 991 (2012-2019)</li>
                  <li>• 992 (2019-present)</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-xl font-bold text-[#D5001C] mb-4">Porsche Boxster/Cayman</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• 986 Boxster (1996-2004)</li>
                  <li>• 987 Boxster/Cayman (2005-2012)</li>
                  <li>• 981 Boxster/Cayman (2012-2016)</li>
                  <li>• 718 Boxster/Cayman (2016-present)</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-xl font-bold text-[#D5001C] mb-4">Porsche SUVs & Sedans</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Cayenne (all generations)</li>
                  <li>• Macan (all models)</li>
                  <li>• Panamera (all generations)</li>
                  <li>• Taycan (electric models)</li>
                </ul>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              We also service all Porsche GT models, limited editions, and classic Porsche vehicles. Our technicians receive continuous training as new Porsche models and technologies are introduced, ensuring we're always equipped to service your specific vehicle properly.
            </p>
            <img 
              src="/assets/assets/images/new/porsche-cayennes/porshe-cayennes3.webp" 
              alt="Professional Porsche repair service" 
              className="w-full rounded-lg shadow-lg"
            />
          </motion.div>

          {/* Porsche Maintenance Cost Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Porsche Maintenance Cost in Dallas</h2>
            <p className="text-gray-700 mb-6">
              Porsche ownership doesn't have to break the bank. Here's what common services cost at our shop vs Park Place Porsche or Porsche of North Houston:
            </p>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-[#D5001C] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service</th>
                    <th className="px-6 py-4 text-left">Dealership Price</th>
                    <th className="px-6 py-4 text-left">Our Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Minor Service (Oil Change)</td>
                    <td className="px-6 py-4 text-gray-600">$400 - $600</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$200 - $350</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Major Service (60k)</td>
                    <td className="px-6 py-4 text-gray-600">$1,800 - $2,500</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$1,200 - $1,600</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Brake Pads & Rotors (Front)</td>
                    <td className="px-6 py-4 text-gray-600">$1,500 - $2,500</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$900 - $1,500</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">IMS Bearing Upgrade</td>
                    <td className="px-6 py-4 text-gray-600">$4,000 - $5,500</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$2,800 - $3,800</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">PDK Transmission Service</td>
                    <td className="px-6 py-4 text-gray-600">$800 - $1,200</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$500 - $750</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Air Suspension Repair</td>
                    <td className="px-6 py-4 text-gray-600">$2,500 - $4,000</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$1,500 - $2,500</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-600 text-sm italic">
              * Prices vary by model. GT3/GT4/Turbo models cost more. These are ballpark figures for 911, Cayenne, and Macan models.
            </p>
          </motion.div>

          {/* Comparison Table */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="vs-dealership" className="text-3xl font-bold text-gray-900 mb-6">Porsche Repair Near Dallas vs. Dealership Service</h2>
            <p className="text-lg text-gray-700 mb-6">
              Many Porsche owners wonder whether an independent European auto specialist can match dealership quality. Our repair center just outside Dallas offers several advantages over dealership service:
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden">
                <thead className="bg-[#D5001C] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service Aspect</th>
                    <th className="px-6 py-4 text-left">Our European Auto Repair Center</th>
                    <th className="px-6 py-4 text-left">Porsche Dealership</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 font-medium">Technician Experience</td>
                    <td className="px-6 py-4 text-green-600">Senior technicians with extensive Porsche-specific experience</td>
                    <td className="px-6 py-4">Variable experience levels from entry-level to senior</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Diagnostic Equipment</td>
                    <td className="px-6 py-4 text-green-600">Full Porsche diagnostic systems</td>
                    <td className="px-6 py-4">Full Porsche diagnostic systems</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Parts Quality</td>
                    <td className="px-6 py-4 text-green-600">Genuine Porsche or premium OEM alternatives</td>
                    <td className="px-6 py-4">Genuine Porsche parts only</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Cost</td>
                    <td className="px-6 py-4 text-green-600">20-40% lower than dealership pricing</td>
                    <td className="px-6 py-4">Premium pricing</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Personal Attention</td>
                    <td className="px-6 py-4 text-green-600">Direct communication with your technician</td>
                    <td className="px-6 py-4">Communication through service advisors</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Track Services Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-[#D5001C] to-red-700 rounded-xl overflow-hidden">
              <div className="p-8">
                <h3 id="track-preparation" className="text-3xl font-bold text-white mb-6 text-center">Track Day Preparation</h3>
                <p className="text-white text-center mb-8 max-w-3xl mx-auto">
                  Whether you're heading to Circuit of the Americas or your local track, we'll ensure your 
                  Porsche is ready to perform at its peak while maintaining reliability.
                </p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                    <h4 className="text-white font-semibold mb-3">Pre-Track Inspection</h4>
                    <ul className="text-white/90 space-y-2 text-sm">
                      <li>• Brake system evaluation</li>
                      <li>• Fluid level check</li>
                      <li>• Suspension inspection</li>
                      <li>• Tire condition assessment</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                    <h4 className="text-white font-semibold mb-3">Performance Upgrades</h4>
                    <ul className="text-white/90 space-y-2 text-sm">
                      <li>• High-temp brake fluid</li>
                      <li>• Track brake pads</li>
                      <li>• Performance alignment</li>
                      <li>• Cooling upgrades</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                    <h4 className="text-white font-semibold mb-3">Post-Track Service</h4>
                    <ul className="text-white/90 space-y-2 text-sm">
                      <li>• Complete inspection</li>
                      <li>• Fluid replacement</li>
                      <li>• Brake evaluation</li>
                      <li>• Data analysis</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Maintenance Recommendations */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="maintenance" className="text-3xl font-bold text-gray-900 mb-6">Porsche Maintenance Recommendations for Dallas Driving Conditions</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-600 rounded-r-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Recommended Porsche Maintenance Adjustments for Dallas Drivers</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong>Oil Changes:</strong> Every 5,000-7,500 miles instead of the standard 10,000 miles due to extreme summer heat
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong>Cooling System Inspection:</strong> Twice yearly before summer and winter temperature extremes
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong>Air Conditioning Service:</strong> Annual system check and refrigerant evaluation before summer heat
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong>Brake Fluid:</strong> Replace every 2 years regardless of mileage (Porsche standard)
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong>PDK Transmission Fluid:</strong> Service every 30,000-40,000 miles (more frequently than Porsche's recommended intervals)
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong>Air Filters:</strong> Replace more frequently due to North Texas high pollen and dust conditions
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Understanding Porsche Technology */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="technology" className="text-3xl font-bold text-gray-900 mb-6">Understanding Porsche Technology: Systems That Require Specialized Repair</h2>
            <p className="text-lg text-gray-700 mb-8">
              Porsche continually introduces technological innovations that require specialized knowledge to service properly. Our European auto repair center near Dallas stays current with these advances:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 id="vario-cam-plus" className="text-xl font-bold text-[#D5001C] mb-3">VarioCam and VarioCam Plus</h4>
                <p className="text-gray-700">
                  Porsche's variable valve timing systems enhance performance, fuel economy, and emissions control. Proper maintenance requires specialized diagnostic tools and procedures our technicians are thoroughly trained to perform.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 id="pdk-dual-clutch-transmission" className="text-xl font-bold text-[#D5001C] mb-3">PDK Dual-Clutch Transmission</h4>
                <p className="text-gray-700">
                  Porsche's PDK transmission offers lightning-fast shifts and superior performance, but requires specialized equipment and expertise for proper diagnosis and service.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 id="pasm" className="text-xl font-bold text-[#D5001C] mb-3">PASM (Porsche Active Suspension Management)</h4>
                <p className="text-gray-700">
                  This electronically variable damping system requires sophisticated diagnostic tools and calibration procedures when servicing Porsche suspension components.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 id="pcm" className="text-xl font-bold text-[#D5001C] mb-3">PCM (Porsche Communication Management)</h4>
                <p className="text-gray-700">
                  Porsche's integrated infotainment system requires specialized programming and coding when components are replaced or updated. Our facility maintains the latest diagnostic systems and software to properly service these complex systems.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Pre-Purchase Inspections */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="pre-purchase-inspections" className="text-3xl font-bold text-gray-900 mb-6">Porsche Pre-Purchase Inspections for Dallas Buyers</h2>
            <p className="text-lg text-gray-700 mb-6">
              Considering a used Porsche in the Dallas area? Our comprehensive pre-purchase inspection service helps protect your investment:
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />150-point mechanical and electronic inspection</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Comprehensive PIWIS diagnostic scan</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />IMS bearing evaluation (on applicable models)</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Chassis and suspension evaluation</li>
              </ul>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Test drive assessment by Porsche specialist</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Service history verification when available</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Model-specific common issue inspection</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Detailed written report with findings and recommendations</li>
              </ul>
            </div>
          </motion.div>

          {/* Testimonial 2 */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 mb-16">
            <blockquote className="text-xl italic text-gray-700 mb-4 text-center">
              "I was considering a 2009 Porsche 911 from a private seller in Dallas. The team at Paradise performed a thorough pre-purchase inspection and discovered the car needed a new clutch and had several oil leaks that weren't obvious during my test drive. Their expertise saved me from significant unexpected expenses and gave me the leverage to negotiate a much better price. The short drive from Dallas was definitely worthwhile."
            </blockquote>
            <p className="text-center font-bold text-gray-900">— David M., Dallas</p>
          </motion.div>

          {/* Performance Optimization */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="performance-optimization" className="text-3xl font-bold text-gray-900 mb-6">Porsche Performance Optimization for Dallas Enthusiasts</h2>
            <p className="text-lg text-gray-700 mb-6">
              Beyond repairs and maintenance, our European auto specialists serving Dallas from Paradise offer performance enhancements that improve your Porsche driving experience:
            </p>
            <ul className="space-y-2 text-gray-700 max-w-3xl">
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Performance software tuning and ECU optimization</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Suspension upgrades and performance alignment</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Exhaust system enhancements</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Brake system performance packages</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />Cooling system upgrades for Texas heat</li>
            </ul>
          </motion.div>

          {/* Complete 911 Service Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Porsche 911 Service & Repair - Air-Cooled to Latest 992</h2>
              <p className="text-lg text-gray-700 mb-6">
                Look, the 911 is the heart and soul of Porsche. We've been working on them since the air-cooled days, and we know these cars inside out.
                Whether you've got a classic 964 that needs some love or a brand new 992 Turbo S, we've got you covered. As part of the Porsche Partner Network,
                we have access to the latest technical bulletins and diagnostic protocols.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-bold text-[#D5001C] mb-3">Air-Cooled 911s (1964-1998)</h4>
                  <p className="text-gray-700 mb-3">
                    These are the classics that everyone dreams about. The 964, 993, and earlier models need special attention. 
                    We're talking about cars where everything is mechanical, and you actually need to know what you're doing. 
                    No computer is going to tell you why that 930 Turbo is running rich - you need experience.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Valve adjustments - yeah, they still need those</li>
                    <li>• Chain tensioner rebuilds before they grenade your engine</li>
                    <li>• CIS fuel injection that actually requires a brain to diagnose</li>
                    <li>• Oil leaks? Every seal, every time. It's just what they do</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-[#D5001C] mb-3">Water-Cooled 911s (996-992)</h4>
                  <p className="text-gray-700 mb-3">
                    The 996 and 997 get a bad rap for IMS bearings, but honestly, we see way more issues with bore scoring 
                    from people who follow Porsche's ridiculous 20,000-mile oil change intervals. The 991 and 992? Bulletproof 
                    if you actually maintain them.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• IMS bearing retrofits - we do them all the time</li>
                    <li>• Bore scoring prevention (hint: change your damn oil)</li>
                    <li>• PDK servicing that dealers want crazy money for</li>
                    <li>• 991/992 transfer case issues nobody talks about</li>
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
                <p className="font-semibold mb-2">Real Talk About 911 Maintenance:</p>
                <p className="text-gray-700">
                  Every 911 owner thinks their car is special. And you know what? They're right. But that doesn't mean 
                  you need to pay dealer markups for basic maintenance. We use the same PIWIS diagnostic system, same quality 
                  parts, and honestly, we probably care more about your car than the 22-year-old kid at the dealership.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Boxster & Cayman Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-blue-50 to-gray-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Boxster & Cayman - The Mid-Engine Workhorses</h2>
              <p className="text-lg text-gray-700 mb-6">
                Don't let anyone tell you the Boxster or Cayman isn't a "real" Porsche. These mid-engine cars are 
                probably the best bang for your buck in the entire lineup. Yeah, they share some parts with the 911, 
                but that's a good thing - means we can fix them without the exotic car tax.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4 shadow-md">
                  <h4 className="font-bold text-[#D5001C] mb-2">986/987 (1996-2012)</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    IMS bearings? Yep, they've got them. But we also see a ton of air/oil separator failures 
                    that nobody mentions. Those plastic coolant tanks? They crack. Every. Single. Time.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-md">
                  <h4 className="font-bold text-[#D5001C] mb-2">981 (2013-2016)</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    Finally ditched the IMS bearing. These are solid cars, but watch out for the timing chain 
                    guides. When they go, it's engine-out time. We've done plenty.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-md">
                  <h4 className="font-bold text-[#D5001C] mb-2">718 (2017+)</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    The turbo 4-cylinders everyone complained about? They're actually pretty bulletproof. 
                    The GT4 and Spyder with the flat-6? Pure gold if you can find one.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Macan Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-green-50 to-gray-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Macan Service - The Gateway Drug</h2>
              <p className="text-lg text-gray-700 mb-6">
                The Macan is how Porsche gets soccer moms hooked on the brand. Built on the Audi Q5 platform, 
                but don't let that fool you - these things drive like a proper Porsche. Plus, being part of the 
                VAG family means we can source parts without the Porsche markup when it makes sense.
              </p>

              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h4 className="font-bold text-[#D5001C] mb-3">Common Macan Issues We Fix:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Transfer case actuators - they fail around 40k miles like clockwork</li>
                  <li>• Coolant thermostat housing - plastic garbage that leaks</li>
                  <li>• PDK clutch pack wear on the turbo models from launch control abuse</li>
                  <li>• Brake squeal that drives everyone nuts (we have the fix)</li>
                </ul>
              </div>

              <p className="text-gray-700">
                Here's what Porsche won't tell you: The base Macan with the 2.0T is basically an Audi engine. 
                Nothing wrong with that - it's reliable and we can service it without the exotic car drama. 
                The S and Turbo models? Now those are proper Porsche engines that need proper care.
              </p>
            </div>
          </motion.div>

          {/* Panamera Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-purple-50 to-gray-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Panamera - The Four-Door 911 That Actually Works</h2>
              <p className="text-lg text-gray-700 mb-6">
                Remember when everyone said a four-door Porsche was sacrilege? Yeah, those people are driving 
                Panameras now. These cars are engineering marvels - air suspension, rear-wheel steering, hybrid 
                systems, the works. Which means when something breaks, you better know what you're doing.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-[#D5001C] mb-3">970 (2010-2016)</h4>
                  <p className="text-gray-700 mb-3">
                    First-gen Panameras are starting to show their age. Coolant distribution pipes fail, 
                    transfer cases need rebuilds, and those air struts? Budget for replacements around 80k miles.
                    The V8s are solid, but the hybrids... let's just say we see a lot of battery issues.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-[#D5001C] mb-3">971 (2017+)</h4>
                  <p className="text-gray-700 mb-3">
                    Second-gen is way better sorted. The new V8 twin-turbo is a beast, and even the base V6 
                    moves like hell. Still seeing some teething issues with the complex electronics, but nothing 
                    major. Yet.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Simplified Cayenne Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gray-100 rounded-xl p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Cayenne Service - The SUV That Saved Porsche</h2>
              <p className="text-lg text-gray-700 mb-6">
                Like it or not, the Cayenne is why Porsche still exists. These trucks print money for Porsche, 
                and honestly, they're damn good vehicles. We see more Cayennes than anything else because there's 
                just so many of them in Dallas. From the VR6 base models to the twin-turbo monsters, we've worked on them all.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="font-bold text-[#D5001C] mb-3">First-Gen (955/957)</h4>
                  <p className="text-gray-700 mb-3">
                    Coolant pipes will fail. Transfer cases will fail. Cardan shaft bearings will fail. 
                    It's not if, it's when. But here's the thing - we stock all the upgraded parts, and we 
                    can usually turn these repairs around same day.
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="font-bold text-[#D5001C] mb-3">Second & Third Gen</h4>
                  <p className="text-gray-700 mb-3">
                    Way better built than the first gen. The 958 and 9Y0 are solid, but watch out for 
                    panoramic roof drains (they clog and flood your interior) and the air suspension 
                    on older models starting to get expensive.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* The Reality Check Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-red-50 to-gray-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Why Paradise-Performance for Your Porsche?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Let's cut through the BS. We're part of the Porsche Partner Network, which means we have the same diagnostic equipment, 
                same technical bulletins, and same training as the dealerships. The difference? We actually give a damn about your car 
                and we're not trying to upsell you on stuff you don't need.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-[#D5001C] mb-3">What We Do Different:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• We'll show you exactly what's wrong - no mysteries</li>
                    <li>• Use OEM or better parts, your choice</li>
                    <li>• Same-day service for most common repairs</li>
                    <li>• Actually answer the phone when you call</li>
                    <li>• Keep your mods and tunes intact (dealers won't)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-[#D5001C] mb-3">Common Sense Maintenance:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Oil changes every 5k miles, not 20k like Porsche says</li>
                    <li>• PDK service at 40k, not 120k</li>
                    <li>• Coolant flushes that actually prevent failures</li>
                    <li>• Brake fluid that doesn't turn to water</li>
                    <li>• Honest assessments about what needs doing now vs later</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Dallas-Focused FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 id="faqs" className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions About Porsche Repair Near Dallas</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">How often should I change the oil in my Porsche in the Dallas area?</h3>
                <p className="text-gray-700">
                  While Porsche recommends oil changes every 10,000 miles, we suggest 5,000-7,500 mile intervals for Dallas drivers due to our extreme heat and stop-and-go traffic conditions. This helps protect your precision engine and extends its lifespan.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Why does my Porsche need special oil?</h3>
                <p className="text-gray-700">
                  Porsche engines require synthetic oils that meet specific Porsche A40 specifications. These oils are formulated to protect high-performance engines operating at elevated temperatures and RPMs. Our European auto repair shop stocks the correct manufacturer-specified oils for all Porsche models.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Is it worth the drive from Dallas to Paradise for Porsche service?</h3>
                <p className="text-gray-700">
                  Many of our Dallas customers tell us the short drive is absolutely worth it for the specialized Porsche expertise, personal attention, and competitive pricing we provide. Plus, our convenient location just outside the Dallas-Fort Worth metroplex means you avoid city traffic while your vehicle is being serviced.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Do you have experience with classic air-cooled Porsche models?</h3>
                <p className="text-gray-700">
                  Yes, our senior technicians have extensive experience with classic air-cooled Porsche engines. We understand the unique maintenance requirements of these iconic vehicles and have the specialized tools required to service them properly.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">How do your Porsche repair prices in Dallas compare to the dealership?</h3>
                <p className="text-gray-700">
                  Our Porsche repair Dallas services typically cost 30-40% less than Dallas Porsche dealerships. We provide the same quality work using genuine parts but without the dealership overhead.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">What areas of Dallas do you serve for Porsche repair?</h3>
                <p className="text-gray-700">
                  We serve all of Dallas and surrounding areas for Porsche service including Highland Park, University Park, North Dallas, Preston Hollow, Uptown, Oak Lawn, Richardson, Plano, Frisco, McKinney, Allen, and the entire DFW Metroplex. Many Dallas Porsche owners make the short drive to Paradise for our expertise.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Do you offer Porsche pre-purchase inspections for Dallas buyers?</h3>
                <p className="text-gray-700">
                  Yes, we provide comprehensive pre-purchase inspections for Dallas area Porsche buyers. Our inspection includes PIWIS diagnostics, mechanical assessment, service history review, and a detailed report to help identify potential issues before purchase.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Service Areas Section - Dallas & Fort Worth */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Porsche Repair Dallas & Fort Worth Service Areas</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-bold text-[#D5001C] mb-4">Porsche Repair Dallas Areas</h3>
                <p className="text-gray-700 mb-4">We provide expert Porsche repair Dallas services to all Dallas neighborhoods:</p>
                <ul className="grid grid-cols-2 gap-2 text-gray-600">
                  <li>• Highland Park</li>
                  <li>• University Park</li>
                  <li>• Preston Hollow</li>
                  <li>• North Dallas</li>
                  <li>• Uptown Dallas</li>
                  <li>• Oak Lawn</li>
                  <li>• Lakewood</li>
                  <li>• Richardson</li>
                  <li>• Plano</li>
                  <li>• Frisco</li>
                  <li>• McKinney</li>
                  <li>• Allen</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">~30 minutes from downtown Dallas</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-bold text-[#D5001C] mb-4">Porsche Repair Fort Worth Areas</h3>
                <p className="text-gray-700 mb-4">We provide expert Porsche repair Fort Worth services to all Tarrant County:</p>
                <ul className="grid grid-cols-2 gap-2 text-gray-600">
                  <li>• Downtown Fort Worth</li>
                  <li>• Southlake</li>
                  <li>• Keller</li>
                  <li>• Colleyville</li>
                  <li>• Grapevine</li>
                  <li>• Westover Hills</li>
                  <li>• Arlington</li>
                  <li>• Bedford</li>
                  <li>• Hurst</li>
                  <li>• Euless</li>
                  <li>• Trophy Club</li>
                  <li>• Roanoke</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">~25 minutes from downtown Fort Worth</p>
              </div>
            </div>
          </motion.div>

          {/* Dallas & Fort Worth CTA Section */}
          <motion.div {...fadeInUp} className="text-center bg-gradient-to-r from-gray-100 to-red-50 rounded-xl p-8">
            <h3 className="text-3xl font-bold mb-6">Dallas & Fort Worth's Premier Porsche Repair Center</h3>
            <p className="text-lg text-gray-700 mb-4 max-w-3xl mx-auto">
              Join hundreds of Dallas and Fort Worth Porsche owners who trust Paradise-Performance for their Porsche repair needs. 
              From routine maintenance to complex repairs, we deliver dealership-quality service at independent shop prices.
            </p>
            <p className="text-xl font-bold text-[#D5001C] mb-8">
              Porsche Repair Dallas | Porsche Repair Fort Worth | PIWIS Diagnostics | IMS Bearing Experts
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-[#D5001C] text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
              >
                Schedule Service
              </Link>
              <a
                href="tel:8173822316"
                className="bg-gray-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition"
              >
                Call (817) 382-2316
              </a>
            </div>
          </motion.div>

          {/* Related Services - Internal Linking for SEO */}
          <motion.div {...fadeInUp} className="mt-16 border-t border-gray-200 pt-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related European Auto Repair Services in Dallas</h3>
            <p className="text-gray-700 mb-6">
              As part of our comprehensive <Link to="/services/european-car-repair" className="text-[#D5001C] hover:underline font-semibold">European auto repair Dallas</Link> services, 
              we also specialize in servicing other premium German and European vehicles. Our Dallas-area customers trust us for:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Link to="/services/european-car-repair/bmw" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-blue-600">BMW Repair Dallas</h4>
                <p className="text-gray-600 text-sm">Expert BMW service for all models. ISTA diagnostics, performance specialists.</p>
              </Link>
              <Link to="/services/european-car-repair/audi" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-red-600">Audi Repair Dallas</h4>
                <p className="text-gray-600 text-sm">Quattro specialists. DSG transmission, TFSI engine experts.</p>
              </Link>
              <Link to="/services/european-car-repair/mercedes" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-gray-800">Mercedes Repair Dallas</h4>
                <p className="text-gray-600 text-sm">STAR diagnostics, AIRMATIC suspension, AMG performance service.</p>
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/services/oil-change" className="text-[#D5001C] hover:underline">Porsche Oil Change Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/transmission-repair" className="text-[#D5001C] hover:underline">PDK Transmission Repair</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/suspension-repair" className="text-[#D5001C] hover:underline">PASM Suspension Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/european-car-repair" className="text-[#D5001C] hover:underline">All European Auto Repair Dallas Services</Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Porsche Vehicles For Sale */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory make="Porsche" limit={4} title="Porsche Vehicles For Sale" />
      </div>
    </>
  );
};

export default PorscheService;
